import React, { useState, useEffect } from 'react';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

// Imports MUI Components
import {  Box,
          Button,
          Stack,
          Typography,
          Link,
          CircularProgress } from "@mui/material"

// Import Assets
import stars from "../../../assets/stars.png";
import back from "../../../assets/back.png";
import logo from "../../../assets/logo.png";

import TextField from '../../../components/UI/atoms/text-fields/TextFieldComp';
import ButtonComp from '../../../components/UI/atoms/button/Button';
import Snackbar from '../../../components/UI/atoms/snackbar/Snackbar';
import Icon from '../../../components/UI/atoms/icon/Icon';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState(false);
    const [lastResetTime, setLastResetTime] = useState(null);
    const [cooldownTime, setCooldownTime] = useState(0);
    const navigate = useNavigate();
    const [noInput, setNoInput] = useState(false);

    // Cooldown timer effect
    useEffect(() => {
        let interval = null;
        if (cooldownTime > 0) {
            interval = setInterval(() => {
                setCooldownTime(cooldownTime => cooldownTime - 1);
            }, 1000);
        } else if (cooldownTime === 0 && lastResetTime) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [cooldownTime, lastResetTime]);

// Email validation function
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Check if user is in cooldown period
  if (cooldownTime > 0) {
    setError(`Please wait ${cooldownTime} seconds before requesting another password reset.`);
    setSnack(true);
    return;
  }

  const trimmedEmail = email.trim();
  setNoInput(false);
  setError('');

  // Validate email input
  if (!trimmedEmail) {
    setNoInput(true);
    setError('Email is required.');
    setSnack(true);
    return;
  }

  // Validate email format
  if (!isValidEmail(trimmedEmail)) {
    setError('Invalid email format.');
    setSnack(true);
    return; // Don't proceed if email format is invalid
  }

  setLoading(true);
  setError('');
  setMessage('');
  const auth = getAuth();
  const db = getFirestore();
  
  try {
    // First, check if email exists in Firestore or Firebase Auth
    // Check Firestore for the email
    const usersRef = collection(db, "users");
    const emailQuery = query(usersRef, where("email", "==", trimmedEmail));
    const authEmailQuery = query(usersRef, where("authEmail", "==", trimmedEmail));
    const pendingEmailQuery = query(usersRef, where("pendingEmailChange", "==", trimmedEmail));

    const [emailSnapshot, authEmailSnapshot, pendingEmailSnapshot] = await Promise.all([
      getDocs(emailQuery),
      getDocs(authEmailQuery),
      getDocs(pendingEmailQuery),
    ]);

    const emailExistsInFirestore = !emailSnapshot.empty || !authEmailSnapshot.empty || !pendingEmailSnapshot.empty;
    
    // Check if email exists in Firebase Auth by attempting to fetch sign-in methods
    let emailExistsInAuth = false;
    try {
      const { fetchSignInMethodsForEmail } = await import('firebase/auth');
      const methods = await fetchSignInMethodsForEmail(auth, trimmedEmail);
      emailExistsInAuth = methods && methods.length > 0;
    } catch (authErr) {
      // If fetchSignInMethodsForEmail fails, assume email might not exist
      // But continue to try sending the reset email
      console.log('Could not verify email in Firebase Auth:', authErr);
    }
    
    // If email doesn't exist in Firestore or Firebase Auth, show error
    if (!emailExistsInFirestore && !emailExistsInAuth) {
      setError('Failed to send password reset link. No match found.');
      setSnack(true);
      setLoading(false);
      return;
    }
    
    // Determine which user document we matched to evaluate verification status
    let matchedUserDoc = null;
    if (!emailSnapshot.empty) {
      matchedUserDoc = emailSnapshot.docs[0];
    } else if (!authEmailSnapshot.empty) {
      matchedUserDoc = authEmailSnapshot.docs[0];
    } else if (!pendingEmailSnapshot.empty) {
      matchedUserDoc = pendingEmailSnapshot.docs[0];
    }

    if (matchedUserDoc) {
      const userData = matchedUserDoc.data();
      const emailVerifiedStatus = userData.emailVerified;
      const pendingEmail = userData.pendingEmailChange || null;
      const verifiedEmail = emailSnapshot.empty ? (authEmailSnapshot.empty ? null : userData.authEmail) : userData.email;

      const isVerifiedEmail = emailVerifiedStatus === "Yes";
      const isMobileOnly = emailVerifiedStatus === "MobileOnly";
      const isPendingEmail = pendingEmail && pendingEmail.toLowerCase() === trimmedEmail.toLowerCase();
      const matchesVerifiedEmail = verifiedEmail && verifiedEmail.toLowerCase() === trimmedEmail.toLowerCase();

      const isUnverifiedAttempt = (matchesVerifiedEmail && !isVerifiedEmail && !isMobileOnly) || isPendingEmail;

      const hasAnyEmail = Boolean(verifiedEmail) || Boolean(pendingEmail);

      if (!hasAnyEmail && !emailExistsInAuth) {
        setError('Failed to send password reset link. No match found.');
        setSnack(true);
        setLoading(false);
        return;
      }

      if (isUnverifiedAttempt) {
        setError('Failed to send password reset link. Email unverified, please verify your email first.');
        setSnack(true);
        setLoading(false);
        return;
      }
    }

    // Email exists and passes verification checks, proceed with sending reset email
    await sendPasswordResetEmail(auth, trimmedEmail);
    setMessage('A password reset link has been sent to your email address. Please check your inbox.');
    // Set cooldown for 1 minute (60 seconds)
    setLastResetTime(Date.now());
    setCooldownTime(60);
    setSnack(true);
  } catch (err) {
    if (err.code === 'auth/user-not-found') {
      setError('Failed to send password reset link. No match found.');
    } else if (err.code === 'auth/invalid-email') {
      setError('Invalid email format.');
    } else {
      setError('Failed to send password reset email. Please try again.');
    }
    setSnack(true);
  } finally {
    setLoading(false);
  }
};

    return (
    <div>
      {/* Head Section */}
      <head>
        <title>VSDiamond Dental Clinic - Forgot Password</title>
      </head>

      {/* All Container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          // backgroundColor: "#2e2e2e",
          backgroundColor: "#FDF9F3",
        }}>

        {/* Upper Stars Container */}
        <Box
          sx={{
            display: {
              xs : "none",
              sm : "none",
              md : "flex",
              lg : "flex",
              xl : "flex",
            },
            width: "100%",
            top: 0,
            position: "absolute",
            justifyContent: "end",
            opacity: "0.5",
          }}>

          <Box
            sx={{
              mr: {
                xs: null,
                sm: null,
                md: "20px",
                lg: "35px",
                xl: "35px"
              }
            }}
          >
            <Icon
              src={stars}
              size="stars"/>
          </Box>

        </Box>

        {/* Lower Stars Container */}
        <Box
          sx={{
            display: {
              xs : "none",
              sm : "none",
              md : "flex",
              lg : "flex",
              xl : "flex",
            },
            width: "100%",
            bottom: 0,
            position: "absolute",
            justifyContent: "start",
            opacity: "0.5",
          }}>

          <Box
            sx={{
              ml: {
                xs: null,
                sm: null,
                md: "20px",
                lg: "35px",
                xl: "35px"
              }
            }}
          >
            <Icon
              src={stars}
              size="stars"/>
          </Box>

        </Box>

        {/* Content Container */}
        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs : "100%",
              sm : "100%",
              md : "700px",
              lg : "700px",
              xl : "700px",
            },
            maxHeight: {
              xs : "100%",
              sm : "100%",
              md : "700px",
              lg : "700px",
              xl : "700px",
            },
            height: "100%",
            margin: {
              xs : null,
              sm : null,
              md : "0 20px",
              lg : "0 20px",
              xl : "0 20px",
            },
            padding: {
              xs : null,
              sm : null,
              md : "15px",
              lg : "15px",
              xl : "15px",
            },
            backgroundColor: "#FEFEFE",
            boxShadow: "0 0 1px 0.1px #2E2D2D",
            borderRadius: 3,
            zIndex: "1",
          }}>

          {/* All Wrapper */}
          <Stack
            direction="column"
            sx={{
              height: "100%",
              // gap: 7,
              justifyContent: "space-between"
            }}>

            {/* Header Container */}
            <Box
              sx={{
                display: "flex",
                margin: {
                  xs : "20px",
                  sm : "20px",
                  md : null,
                  lg : null,
                  xl : null,
                },
                // border: "1px solid black",
              }}>
                
                {/* Link to Home Page*/}
                <Link 
                  href="/#"
                  className="links"
                  style={{ 
                    textDecoration: "none" 
                  }}>
                  
                  {/* Link to Home Page Container */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "start",
                      alignItems: "center",
                      textDecoration: "none",
                      padding: "10px",
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.6,
                        borderRadius: 2,
                        transition: "background-color 0.3s",
                      },
                    }}>

                    {/* Image */}
                    <img
                      src={back}
                      alt="home"
                      className="back-text"
                      style={{
                        transition: "filter 0.2s, width 0.2s, height 0.2s"
                      }}/>

                    {/* Text */}
                    <Typography
                      variant="body1"
                      className="home-text"
                      sx={{
                        color: "#2C251E",
                        fontWeight: "300",
                        fontSize: "14px",
                        padding: "0px 5px",
                        transition: "font-size 0.2s",
                        // border: "1px solid black"
                      }}>Home
                    </Typography>

                  </Box>

                </Link>

            </Box>

            {/* Body Container */}
            <Box
              sx={{
                textAlign: "center",
                justifyItems: "center",
                padding: {
                  xs : "15px",
                  sm : "15px",
                  md : null,
                  lg : null,
                  xl : null,
                },
                // border: "1px solid black"
                alignContent: "center",
              }}>

              {/* Logo Container */}
              <Box>
                <img src={logo} alt="logo" className="logos"/>
              </Box>

              {/* Heading Message */}
              <Typography 
                variant="h2"
                sx={{
                  color: "#2C251E",
                  fontSize: {
                    xs: "23px",
                    sm: "30px",
                    md: "30px",
                    lg: "30px",
                    xl: "30px",
                  },
                  padding: "5px 25px",
                  // backgroundColor: "#896c40ff",
                }}>Forgot Password?
              </Typography>

              {/* Subheading Message */}
              <Typography 
                variant="h3"
                sx={{
                  color: "#59544A",
                  fontWeight: "300",
                  fontSize: "17px",
                  padding: "5px 15px",
                  // backgroundColor: "#896c40ff",
                }}>Enter your email address.
              </Typography> 

              {/* Forms Container */}
              <Stack
                spacing={4}
                sx={{
                  width: {
                    xs: "90%",
                    sm: "50%",
                    md: "50%",
                    lg: "50%",
                    xl: "50%",
                  },
                  // alignItems: "center",
                  textAlign: "center",
                  marginTop: "20px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}>

                {/* Forms TextBoxes Container */}
                <Stack
                  direction="column"
                  spacing={6}
                  sx={{
                    width: "100%"
                  }}>

                <form onSubmit={handleSubmit}>

                    <Box
                        sx={{
                            marginBottom: "20px",
                            pb: "20px"
                        }}>
                  {/* TextField Email Container*/}

                            <TextField
                              placeholder="Your email"
                              id="email"
                              label="Email"
                              type="email"
                              value={email}
                              color="primary"
                              onChange={(newValue) => {
                      setEmail(newValue);

                      const trimmed = newValue.trim();

                      if (!trimmed) {
                        setNoInput(true);
                        setError("Email is required.");
                        return;
                      }

                      if (!isValidEmail(trimmed)) {
                        setError("Invalid email format.");
                        setNoInput(false);
                        return;
                      }

                      // When format becomes valid:
                      setError("");
                      setNoInput(false);
                    }}
                              state={false}
                              requires={false}
                    check={error}/>


                  </Box>

                  <Box
                    // display='flex'
                    sx={{
                      justifyContent: "center",
                    }}>

                  {/* Send Password Reset Link Button */}
                  <ButtonComp
                    buttonId="signIn"
                    variant="contained"
                    noElevate={true}
                    noRipple={true}
                    label={
                      loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                          <span>Sending...</span>
                        </Box>
                      ) : cooldownTime > 0 ? (
                        `Send Link Cooldown: (${cooldownTime})s`
                      ) : "Send Reset Link"
                    }
                    color="light"
                    paddings="default"
                    background="default"
                    font="default"
                    weight="default"
                    border="default"
                    transform="default"
                    selected={true}
                    keys={false}
                    hoverTextColor="light"
                    hoverBackground="dark"
                    hoverBorder="default"
                    clickPass={handleSubmit}
                    noRead={loading || cooldownTime > 0}
                    type="default"
                    justify="default"
                    iconned={false}
                    destination="none"
                    open={true}/>

</Box>

                </form>

                  </Stack>

                    <Link 
                      href="/sign-in"
                      underline="hover"
                      sx={{
                        fontSize: "14px",
                        color: "#C5A572"
                      }} 
                    //   variant="body2"
                    >
                      Back to Sign In
                    </Link>

                </Stack>

              </Box>
      
            {/* Footer Container */}
            <Box
              sx={{
                display: "flex",
                // border: "1px solid black",
                padding: "5px",
              }}>
            </Box>

          <Snackbar
            open={snack}
            message={message || error}
            background={
              message ? "success" :
              "error"
            }
            onClose={() => setSnack(false)}
          />

        </Stack>
        </Box>
        </Box>
    </div>  
    );
};

export default ForgotPassword;